import { Box, Grid, Link, Paper, Stack } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { REPORT_VALUES, reportHref } from "../../constants";
import { resolveAccountId } from "../../session";
import { v2Colors } from "../../theme/v2Theme";
import PillButton from "../generic/PillButton";
import ProgressRow from "../generic/ProgressRow";

const COUNT_SIMS_USED_90_DAYS_HREF = reportHref(REPORT_VALUES.countSimsUsed, {
  days: 90,
});

const SHARE_SIMS_ICON = "fas fa-share";
const VIEW_LIBRARY_ICON = "fas fa-list";

const ProgressList = ({ items, barColor }) => {
  if (!items?.length) {
    return <p className="edsimsV2-tile-body">No simulations played yet.</p>;
  }

  return (
    <Stack spacing="18px">
      {items.map((sim, idx) => (
        <Box key={sim.label}>
          <ProgressRow
            label={sim.label}
            value={sim.value}
            displayValue={sim.count != null ? String(sim.count) : undefined}
            barColor={barColor}
          />
          {idx < items.length - 1 ? (
            <Box className="edsimsV2-sim-progress-divider" />
          ) : null}
        </Box>
      ))}
    </Stack>
  );
};

const SimulationLibraryEmptyState = ({ globalSims = [] }) => (
  <Box className="edsimsV2-sim-library-empty">
    <Box className="edsimsV2-sim-library-empty-copy">
      <p className="edsimsV2-sim-library-empty-badge">
        <i className="fas fa-check" aria-hidden="true" />
        Account ready · Let’s get started
      </p>
      <Box component="h2" className="edsimsV2-sim-library-empty-title">
        You haven’t run any simulations yet
      </Box>
      <p className="edsimsV2-sim-library-empty-body">
        Browse the simulation library to find the right scenarios for your
        cohorts. Once you run your first simulation, your dashboard will come
        to life with activity, progress, and insights.
      </p>
      <Stack
        direction="row"
        spacing="12px"
        flexWrap="wrap"
        useFlexGap
        className="edsimsV2-sim-library-empty-actions"
      >
        <PillButton
          label="View Library"
          href="/sims"
          openInNewTab
          variant="primary"
          startIconClass={VIEW_LIBRARY_ICON}
        />
        <PillButton
          label="Share SIMS"
          href={`/simulations/link/0/${resolveAccountId()}`}
          variant="soft"
          startIconClass={SHARE_SIMS_ICON}
        />
      </Stack>
    </Box>
    <Box className="edsimsV2-sim-library-empty-global">
      <Stack spacing="20px">
        <p className="edsimsV2-sim-section-caption">Global top simulations</p>
        <ProgressList items={globalSims} barColor={v2Colors.progressGreen} />
      </Stack>
    </Box>
  </Box>
);

const SimulationLibraryTile = ({
  clientSims = [],
  globalSims = [],
  totalCount = 0,
}) => {
  const isEmpty =
    !(Number(totalCount) > 0) && !(clientSims && clientSims.length > 0);

  if (isEmpty) {
    return (
      <Paper
        className="edsimsV2-sim-library-card edsimsV2-sim-library-card--empty"
        elevation={0}
      >
        <SimulationLibraryEmptyState globalSims={globalSims} />
      </Paper>
    );
  }

  return (
    <Paper className="edsimsV2-sim-library-card" elevation={0}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
        spacing="12px"
      >
        <Box className="edsimsV2-sim-library-header">
          <Box component="h2" className="edsimsV2-tile-title">
            Simulation library
          </Box>
          <p className="edsimsV2-sim-library-subtitle">Top played simulations</p>
        </Box>
        <Stack direction="row" spacing="12px" flexWrap="wrap" useFlexGap>
          <PillButton
            label="View Library"
            href="/sims"
            openInNewTab
            variant="primary"
            startIconClass={VIEW_LIBRARY_ICON}
          />
          <PillButton
            label="Share SIMS"
            href={`/simulations/link/0/${resolveAccountId()}`}
            variant="soft"
            startIconClass={SHARE_SIMS_ICON}
          />
        </Stack>
      </Stack>

      <Grid container columnSpacing="32px" rowSpacing="20px" sx={{ flex: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing="20px">
            <Link
              component={RouterLink}
              to={COUNT_SIMS_USED_90_DAYS_HREF}
              underline="hover"
              className="edsimsV2-sim-section-caption edsimsV2-sim-section-caption--link"
            >
              Top 3 Client Simulations
            </Link>
            <ProgressList
              items={clientSims}
              barColor={v2Colors.progressPurple}
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing="20px">
            <p className="edsimsV2-sim-section-caption">Global top simulations</p>
            <ProgressList items={globalSims} barColor={v2Colors.progressGreen} />
          </Stack>
        </Grid>
      </Grid>

      <Link
        component={RouterLink}
        to={COUNT_SIMS_USED_90_DAYS_HREF}
        underline="hover"
        className="edsimsV2-cohort-dashboard-link"
      >
        View last 90 days report →
      </Link>
    </Paper>
  );
};

export default SimulationLibraryTile;
